import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { environment } from '@environments/environment';
import { catchError, Observable, retry } from 'rxjs';
import { SessionStorageService } from './storage.service';
import { UserService } from './user.service';

export interface ICartingRuleSortItem {
	priority: number;
	order: 0 | 1;
	type: {
		self: '/carting/configuration-sorting-strategies/profileLength' | '/carting/configuration-sorting-strategies/productionSequenceNumber';
	};
}

export interface ICartingConfigs {
	self: string;
	maxProductionSequenceNumberJumpsInSameWagon: boolean;
	splitDoorAndWindowSashes: boolean;
	groupByColors: boolean;
	groupSorting: [ICartingConfigsGroupSorting];
	componentSorting: [ICartingConfigsComponentSorting];
}

export interface ICartingConfigsGroupSorting {
	priority: number;
	order: 0 | 1;
	type: { self: '/carting/config-group-sorting-strategies/productionSequenceNumber' };
}

export interface ICartingConfigsComponentSorting {
	priority: number;
	order: 0 | 1;
	type: {
		self: '/carting/config-component-sorting-strategies/frame-field' | '/carting/config-component-sorting-strategies/height' | '/carting/config-component-sorting-strategies/width' | 'carting/config-component-sorting-strategies/surface-area';
	};
}

export interface ICartingConfigsResponse {
	items: [ICartingConfigs];
}

export interface IWagon {
	self: string;
	name: string;
	description: string;
	batchType: string;
	updatedDate: string;
	updatedAtEpoch?: number; // kept beside updatedDate by the lists, which sort by it; a new or imported item has none yet
	updatedBy: string;
	cells: number;
	type: string;
	translationType: string;
	layer: string;
	translationLayer: string;
}

export interface IWagons {
	self: string;
	items: IResponseWagon[];
}

export interface IBaseWagon {
	self: string;
	name: string;
	description: string;
	batchType?: {
		self: string;
	};
	configuration: {
		cells: number;
		type: {
			self: string;
		};
		layer: {
			self: string;
		};
	};
}

export interface IRequestWagon extends IBaseWagon {
	// No additional properties
}

export interface IMeta {
	meta: {
		createdAt: number;
		createdBy: {
			self: string;
		};
		updatedAt: number;
		updatedBy: {
			self: string;
		};
		version: number;
	};
}

export interface ITag {
	name: string;
	value: string;
}

export interface IResponseWagon extends IBaseWagon, IMeta {
	tags: ITag[];
}

export interface IWagonReference {
	wagon: {
		self: string;
	};
	priority?: number;
}

export interface IWagonset {
	self: string;
	name: string;
	description: string;
	wagons: MatTableDataSource<IWagonReference>;
	selectedWagons: string[];
	updatedDate: string;
	updatedBy: string;
}

export interface IWagonDetails {
	name: string;
	description: string;
	cells: string;
	rows: string;
	columns: string;
	components: string;
	updatedDate: string;
}

export interface IBaseWagonset {
	self: string;
	name: string;
	description: string;
	wagons: IWagonReference[];
}

export interface IResponseWagonset extends IBaseWagonset, IMeta {
	tags: ITag[];
}

export interface IRequestWagonset extends IBaseWagonset {
	// No additional properties
}

export interface IWagonsets {
	self: string;
	items: IResponseWagonset[];
}

export interface IRule {
	self: string;
	name: string;
	description: string;
	batchType: string;
	material: string;
	machine: string;
	profile: string;
	wagonset: string;
	placementPriority: number | null;
	segregation: string;
	optimization: string;
	sorting: ICartingRuleSortItem[];
	updatedDate: string;
	updatedBy: string;
	id0: string;
	id1: string;
	id2: string;
	id3: string;
	id4: string;
	id5: string;
	id6: string;
	id7: string;
	id8: string;
	id9: string;
	state: 'Any' | 'NoGeometry' | 'OnlyGeometry';
}

export interface IRules {
	self: string;
	items: IResponseRule[];
}

export interface IBaseRule {
	self: string;
	name: string;
	description: string | null;
	batchType: {
		self: string;
	};
	profile: {
		self: string;
	} | null;
	wagonset: {
		self: string;
	} | null;
	default: {
		identifiers: [string, string, string, string, string, string, string, string, string, string];
		state: 'Any' | 'NoGeometry' | 'OnlyGeometry';
	} | null;
	aluminium: {
		material: {
			self: string;
		};
		machine: {
			self: string;
		};
	};
	placementPriority: number | null;
	segregation: {
		identifier: string;
	};
	optimization: {
		identifier: string;
	};
	sorting: ICartingRuleSortItem[];
}

export interface IResponseRule extends IBaseRule, IMeta {
	tags: ITag[];
}

export interface IRequestRule extends IBaseRule {
	// No additional properties
}

@Injectable({
	providedIn: 'root',
})
export class CartingService {
	public constructor(
		private httpClient: HttpClient,
		private userService: UserService,
		private sessionStorageService: SessionStorageService,
	) {}

	public getConfigs(): Observable<ICartingConfigsResponse> {
		const URL = `${environment.awsEnvUri}/carting/configs`;
		return this.httpClient
			.get<ICartingConfigsResponse>(URL, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public getWagons(): Observable<IWagons> {
		const URL = `${environment.awsEnvUri}/carting/wagons`;
		return this.httpClient
			.get<IWagons>(URL, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public getRules(): Observable<IRules> {
		const apiUrl = `${environment.awsEnvUri}/carting/rules`;
		return this.httpClient
			.get<IRules>(apiUrl, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	createRule(rule: IRule): Observable<IRequestRule> {
		const body: IRequestRule = this.mapRuleToRequestRule(rule);
		const apiUrl = `${environment.awsEnvUri}/carting/rules`;
		return this.httpClient
			.post<IRequestRule>(apiUrl, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	updateRule(rule: IRule): Observable<IResponseRule> {
		const body: IRequestRule = this.mapRuleToRequestRule(rule);
		const apiUrl = environment.awsEnvUri + rule.self;
		return this.httpClient
			.put<IRequestRule>(apiUrl, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public updateWagon(wagon: IWagon): Observable<IResponseWagon> {
		const body: IRequestWagon = this.mapWagonToRequestWagon(wagon);
		const apiUrl = environment.awsEnvUri + wagon.self;
		return this.httpClient
			.put<IRequestWagon>(apiUrl, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public createWagon(wagon: IWagon): Observable<IRequestWagon> {
		const body: IRequestWagon = this.mapWagonToRequestWagon(wagon);
		const apiUrl = `${environment.awsEnvUri}/carting/wagons`;
		return this.httpClient
			.post<IRequestWagon>(apiUrl, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public delete(reference: string): Observable<any> {
		const apiUrl = environment.awsEnvUri + reference;
		return this.httpClient
			.delete(apiUrl, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public updateConfig(config: ICartingConfigs): Observable<ICartingConfigs> {
		const SLASH = config.self.lastIndexOf('/');
		const UUID = config.self.substring(SLASH + 1);
		const URL = `${environment.awsEnvUri}/carting/configs/${UUID}`;
		return this.httpClient
			.put<ICartingConfigs>(URL, config, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	private getHeaders() {
		const accountId: string = this.userService.account.id;
		const token: string = this.sessionStorageService.get('User.Authorization');
		return new HttpHeaders({
			Authorization: `Bearer ${token}`,
			'x-account-id': accountId,
		});
	}

	private handleError(error: HttpErrorResponse) {
		throw error;
		return [];
	}

	public getLastSegment(urlOrObject: string | any, keyPath?: string[]) {
		let url: string;

		if (typeof urlOrObject === 'string') {
			url = urlOrObject;
		} else if (Array.isArray(keyPath)) {
			url = keyPath.reduce((acc, key) => acc[key], urlOrObject);
		} else {
			throw new TypeError('Invalid arguments');
		}

		const parts = url.split('/');
		return parts[parts.length - 1];
	}

	public getWagonsets(): Observable<IWagonsets> {
		const apiUrl = `${environment.awsEnvUri}/carting/wagonsets`;
		return this.httpClient
			.get<IWagonsets>(apiUrl, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	createWagonset(wagonset: IWagonset): Observable<IRequestWagonset> {
		const body: IRequestWagonset = toRequestWagonset(wagonset);
		const apiUrl = `${environment.awsEnvUri}/carting/wagonsets`;
		return this.httpClient
			.post<IRequestWagonset>(apiUrl, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public updateWagonset(wagonset: IWagonset): Observable<IResponseWagonset> {
		const body: IRequestWagonset = toRequestWagonset(wagonset);
		const apiUrl = environment.awsEnvUri + wagonset.self;
		return this.httpClient
			.put<IRequestWagonset>(apiUrl, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	removeWagonFromWagonset(wagonset: IWagonset): Observable<IResponseWagonset> {
		const body: IRequestWagonset = {
			self: wagonset.self,
			name: wagonset.name,
			description: wagonset.description,
			wagons: wagonset.wagons.data.map((ref) => ({ wagon: { self: ref.wagon.self } })),
		};
		const apiUrl = environment.awsEnvUri + wagonset.self;
		return this.httpClient
			.put<IRequestWagonset>(apiUrl, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	private getEngine() {
		return this.userService.account.defaultEngine;
	}

	mapWagonToRequestWagon(wagon: IWagon): IRequestWagon {
		const defaultEngine = this.getEngine();
		return {
			self: wagon.self,
			name: wagon.name,
			description: wagon.description,
			...(wagon.batchType && {
				batchType: {
					self: `/mdm/apps/one/stores/${defaultEngine}/production-batch-types/${wagon.batchType}`,
				},
			}),
			configuration: {
				cells: wagon.cells,
				type: { self: wagon.type },
				layer: { self: wagon.layer },
			},
		};
	}

	mapRuleToRequestRule(rule: IRule): IRequestRule {
		const defaultEngine = this.getEngine();
		const body: IRequestRule = {
			self: rule.self,
			name: rule.name,
			description: rule.description,
			batchType: {
				self: `/mdm/apps/one/stores/${defaultEngine}/production-batch-types/${rule.batchType}`,
			},
			...(rule.material &&
				rule.machine && {
					aluminium: {
						material: {
							self: `/mdm/apps/one/stores/${defaultEngine}/component-inventory-items/${rule.material}`,
						},
						machine: {
							self: `/mdm/apps/one/stores/${defaultEngine}/production-machines/${rule.machine}`,
						},
					},
				}),
			...(rule.profile && {
				profile: {
					self: `/mdm/apps/one/stores/${defaultEngine}/component-profiles/${rule.profile}`,
				},
			}),
			...(rule.state && {
				default: {
					identifiers: [rule.id0, rule.id1, rule.id2, rule.id3, rule.id4, rule.id5, rule.id6, rule.id7, rule.id8, rule.id9],
					state: rule.state,
				},
			}),
			...(rule.wagonset && {
				wagonset: {
					self: rule.wagonset,
				},
			}),
			placementPriority: rule.placementPriority,
			...(rule.segregation && {
				segregation: {
					identifier: rule.segregation,
				},
			}),
			...(rule.optimization && {
				// This property is not implemented
				optimization: {
					identifier: rule.optimization,
				},
			}),
			sorting: rule.sorting,
		};
		return body;
	}
}

function toRequestWagonset(wagonset: IWagonset): IRequestWagonset {
	const wagonsData = Array.isArray(wagonset.wagons) ? wagonset.wagons : Array.isArray(wagonset.wagons?.data) ? wagonset.wagons.data : [];

	return {
		self: wagonset.self,
		name: wagonset.name,
		description: wagonset.description,
		wagons: wagonsData.map((ref, index) => ({
			wagon: { self: ref.wagon?.self ?? ref },
			priority: ref.priority ?? index + 1,
		})),
	};
}
